import {
  addOfflineOperation,
  removeOfflineOperation,
  showOfflineDeletionPopOver,
  updateOfflineOperation,
} from './../utils/states-management/actions/offline.action';
import { ConnectionState, nextOfflineIndex, selectIsSyncInProgress } from './../utils/states-management/reducers/offline.reducer';
import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpXsrfTokenExtractor,
} from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, Subscription, throwError, timer, lastValueFrom } from 'rxjs';
import { tap, catchError, finalize, distinctUntilChanged, take, switchMap, filter, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LOGOUT } from '../utils/states-management/actions/owner.action';
import { ConnectionService } from '../services/connection.service';
import { ToastUtils } from '../utils/toast_builder';
import { SessionTokenService } from './session-token.service';


@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private pendingRequests = 0;
  private consecutiveEmptyChecks = 0;
  private slowRequestTimer: Subscription | null = null;
  private isOnline = false;
  private isTestingConnection = false;
  private lastConnectionState: 'good' | 'poor' | 'offline' = 'good';
  ConnectionStateChecker: 'good' | 'poor' | 'offline' = 'good'
  private isSyncInProgress = false;
  private isLoggingOut = false;

  constructor(
    public router: Router,
    private store: Store<{ connection: ConnectionState }>,
    private tokenExtractor: HttpXsrfTokenExtractor,
    private connectionService: ConnectionService,
    private toastUtils: ToastUtils,
    private sessionToken: SessionTokenService

  ) {
    this.store.select('connection').pipe(
      distinctUntilChanged((a, b) =>
        a.isOnline === b.isOnline &&
        a.connectionQuality === b.connectionQuality
      )
    ).subscribe(({ isOnline, connectionQuality }) => {
      this.isOnline = isOnline;
      this.lastConnectionState = connectionQuality;
    });

    this.store.select(selectIsSyncInProgress).subscribe(isSyncInProgress => {
      this.isSyncInProgress = isSyncInProgress;
    });

    this.connectionService.connectionTesting$.subscribe(isTesting => {
      this.isTestingConnection = isTesting;
    })
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authSessionReq = this.cloneRequestWithSessionTokens(req);

    if (this.shouldSkipInterceptor(req.url)) {
      return next.handle(req);
    }

    if (req.method === 'GET' && this.isSyncInProgress) {
      return EMPTY;
    }

    if (this.isOnline) {

      const authReq = this.cloneRequestWithTokens(authSessionReq);
      this.consecutiveEmptyChecks = 0;
      const requestStart = Date.now();
      this.setupRequestTimers(authReq);


      // Use a single timer to handle long requests without blocking
      const longRequestTimer = timer(10000).subscribe(() => {
        if (!this.isTestingConnection) {
          // console.log('Interceptor request took more than 10s, triggering connection check');
          if (this.isModificationMethod(req.method)) {
            // console.log(`Request blocked during offline state: ${req.method} ${req.url}`);
            this.manageOfflineOperation(req);
          }
          this.connectionService.requestConnectionCheck();
        }
      });
      return next.handle(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (this.isNetworkError(error) && this.isModificationMethod(req.method)) {
            // console.log('Network error detected, storing operation offline');
            // this.manageOfflineOperation(req);
            // TOCHANGE
            return EMPTY;
          }
          // if (this.isSessionError(error)) {
          //   return this.handle401(error, authReq, next);
          // }
          return this.handleError(error);
        }),
        finalize(() => {
          this.cleanupRequest();
          longRequestTimer.unsubscribe();
          const requestDuration = Date.now() - requestStart;

          // Trigger connection check based on request duration and last connection state
          if (!this.isTestingConnection &&
            ((requestDuration < 3000 && (this.lastConnectionState === 'offline' || this.lastConnectionState === 'poor')) ||
              (requestDuration > 5000 && this.lastConnectionState !== 'poor'))) {
            // console.log('Interceptor request Connection Check triggered');
            this.connectionService.requestConnectionCheck();
          }
        })
      );
    } else {
      if (this.isModificationMethod(req.method)) {
        // console.log(`Request blocked during offline state: ${req.method} ${req.url}`);
        this.manageOfflineOperation(req);
        return EMPTY;
      } else {
        return EMPTY;

      }
    }

  }

  private shouldSkipInterceptor(url: string): boolean {
    const skipUrls = [
      'https://us1.locationiq.com/v1/reverse',
      'api/get_language/',
      '/api/test-connection',
      'speed.cloudflare',
      '/check-version',
      'static.userback.io'
    ];
    return skipUrls.some(skipUrl => url.includes(skipUrl));
  }

  private isModificationMethod(method: string): boolean {
    return ['POST', 'DELETE', 'PUT'].includes(method);
  }


  async manageOfflineOperation(request) {

    if (request.method === 'DELETE' && request.url.split('offline-')[1] || request.url.includes('delete-farm-production/undefined')) {
      if (!request.url.includes('delete-cashflow')) {
        // this.store.dispatch(showOfflineDeletionPopOver());
        return EMPTY;
      }
      const id = request.url.split('offline-')[1];
      this.store.dispatch(removeOfflineOperation({ id }));
      return EMPTY;
    }
    // else 
    if (request.method === 'PUT' || request.method === 'POST') {
      // Prepare updated data to merge into existing offline operation
      let updatedFormDataObject: any = null;
      if (request?.body instanceof FormData) {
        updatedFormDataObject = {};
        for (const [key, value] of request.body.entries()) {
          updatedFormDataObject[key] = value;
        }
      }
      if (updatedFormDataObject?.id && updatedFormDataObject?.id.includes('offline')) {
        const id = updatedFormDataObject['id'].split('offline-')[1];

        delete updatedFormDataObject.id
        this.store.dispatch(updateOfflineOperation({ id, updatedFormDataObject }));
        return EMPTY;
      }

    }
    let formDataObject = null;

    if (!request.tempId) {
      request.tempId = await lastValueFrom(this.store.select(nextOfflineIndex).pipe(take(1)));
    }

    if (request?.body instanceof FormData) {
      formDataObject = {};
      for (const [key, value] of request?.body?.entries()) {
        formDataObject[key] = value;
      }
    }
    if (formDataObject) {
      request.formDataObject = formDataObject;
    }
    if (request.method === 'DELETE' && !request.url.includes('delete-cashflow')) {
      // this.store.dispatch(showOfflineDeletionPopOver());
      return EMPTY;
    } else {
      this.store.dispatch(addOfflineOperation({ operation: request }));
    }


  }

  private cloneRequestWithTokens(req: HttpRequest<any>): HttpRequest<any> {
    const token = this.tokenExtractor.getToken();
    return token ? req.clone({
      headers: req.headers.set('X-XSRF-TOKEN', token),
      withCredentials: true
    }) : req.clone({ withCredentials: true });
  }
  private cloneRequestWithSessionTokens(req: HttpRequest<any>): HttpRequest<any> {
    const sessionToken = this.getSessionToken();
    return sessionToken ? req.clone({ headers: req.headers.set('X-Session-Token', sessionToken) }) : req;
  }
  public getSessionToken() {
    const match = document.cookie.match(new RegExp('(^|; )' + SessionTokenService.STORAGE_KEY + '=([^;]+)'));
    const sessionToken = match ? decodeURIComponent(match[2]) : null;
    return sessionToken;
  }
  // TODO: Incomplete handleError
  private handleError(error: HttpErrorResponse): Observable<never> {
    if (this.isSessionError(error)) {
      if (!this.isLoggingOut) {
        this.isLoggingOut = true;
        this.store.dispatch({ type: LOGOUT, redirection_path: '/' });
      }

      // this.toastUtils.toastBuilder('Error', 'Authentication Error', 'Your session has expired. Please log in again.', 'danger');
    } else if (error.status === 404) {
      // this.toastUtils.toastBuilder('Error', 'Not Found', 'The requested resource could not be found.', 'danger');
    } else if (error.status == 500) {
      // this.toastUtils.toastBuilder('Error', 'Network Error', 'Unable to connect to the server. Please check your internet connection.', 'danger');
    } else if (error.status >= 400 && error.status < 500) {
      // Client-side errors
      const errorMessage = error.error?.message || error.statusText || 'An unexpected client error occurred.';
      // this.toastUtils.toastBuilder('Error', `Client Error (${error.status})`, errorMessage, 'danger');
    } else if (this.isNetworkError(error)) {
      this.connectionService.requestConnectionCheck();
      // this.toastUtils.toastBuilder('Error', 'Network Error', 'Unable to connect to the server. Please check your internet connection.', 'danger');
    } else {
      // Other server-side errors
      const errorMessage = error.error?.message || error.statusText || 'An unexpected server error occurred.';
      // this.toastUtils.toastBuilder('Error', `Server Error (${error.status})`, errorMessage, 'danger');
    }

    return throwError(() => error);
  }
  private isSessionError(err: HttpErrorResponse): boolean {
    return err.status === 401 || err.status === 403;
  }
  private setupRequestTimers(req) {
    this.slowRequestTimer?.unsubscribe();
    this.slowRequestTimer = timer(4000).subscribe(() => {
      if (!this.isTestingConnection) {
        this.connectionService.requestConnectionCheck();
      }
    });

    // Handle image upload timeout separately
    if (this.isImageUpload(req)) {
      this.handleImageUploadTimeout();
    }
  }

  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  private handle401(err: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.isRefreshing) {

      return this.refreshSubject.pipe(
        filter(t => t !== null),
        take(1),
        switchMap(t => next.handle(req.clone({ headers: req.headers.set('X-Session-Token', t!) })))
      );

    } else {

      this.isRefreshing = true;
      this.refreshSubject.next(null);
      return this.sessionToken.generate().pipe(
        switchMap(t => {
          this.isRefreshing = false;
          this.refreshSubject.next(t);
          return next.handle(req.clone({ headers: req.headers.set('X-Session-Token', t) }));
        }),
        catchError(refreshErr => {
          this.isRefreshing = false;
          return this.handleError(refreshErr);
        })
      );
    }
  }
  private isNetworkError(error: HttpErrorResponse): boolean {
    return error.status === 0 || error.status >= 500;
  }

  private cleanupRequest() {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    this.slowRequestTimer?.unsubscribe();
    this.slowRequestTimer = null;

    if (this.pendingRequests === 0) {
      this.handleIdleState();
    }
  }

  private handleIdleState() {
    this.consecutiveEmptyChecks++;

    if (this.consecutiveEmptyChecks >= 3) {
      this.connectionService.stopConnectionQualityMonitoring();
      this.consecutiveEmptyChecks = 0; // Reset counter after pausing
    }
  }

  private isImageUpload(req: HttpRequest<any>): boolean {
    return req.body instanceof FormData &&
      Array.from(req.body.entries()).some(([_, value]) =>
        value instanceof Blob || (typeof value === 'string' && value.startsWith('data:image'))
      );
  }

  private handleImageUploadTimeout() {
    const imageUploadTimer = timer(30000).subscribe(() => {
      this.connectionService.requestConnectionCheck();
    });
    return imageUploadTimer;
  }
}
